<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Gestión de Categorías
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ categorias.length }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 font-medium mt-0.5">Administra las categorías de productos</p>
      </div>
      
      <!-- Botones de acción -->
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap">
        <!-- Menú Exportar -->
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-900 hover:to-black transition-all duration-300 shadow hover:shadow-md text-[10px] sm:text-xs font-black tracking-widest"
          >
            <Icon icon="mdi:printer" class="mr-1.5 sm:mr-2 text-sm sm:text-base" />
            <span class="hidden xs:inline">Imprimir</span>
            <span class="xs:hidden">Imprimir</span>
            <Icon icon="mdi:chevron-down" class="ml-1.5 sm:ml-2 text-sm" />
          </button>
          
          <!-- Menú Exportar -->
          <div v-if="showExportMenu" 
               class="absolute right-0 mt-2 w-56 bg-gray-900 rounded-xl shadow-lg border border-gray-700 z-50">
            <div class="py-2">
              <button
                @click="printFullReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200 text-xs"
              >
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-500 text-lg" />
                <div>
                  <p class="font-black">Reporte Completo</p>
                  <p class="text-[9px] text-gray-400">Con estadísticas y tabla</p>
                </div>
              </button>
              <button
                @click="printSimplifiedReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200 text-xs"
              >
                <Icon icon="mdi:table" class="mr-3 text-blue-500 text-lg" />
                <div>
                  <p class="font-black">Listado Simplificado</p>
                  <p class="text-[9px] text-gray-400">Solo tabla de categorías</p>
                </div>
              </button>
              <div class="border-t border-gray-700 my-2"></div>
              <button
                @click="printCurrentView"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200 text-xs"
              >
                <Icon icon="mdi:eye" class="mr-3 text-green-500 text-lg" />
                <div>
                  <p class="font-black">Vista Actual</p>
                  <p class="text-[9px] text-gray-400">Con filtros aplicados</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <button
          @click="openCreateModal"
          class="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow hover:shadow-md text-[10px] sm:text-xs font-black tracking-widest"
        >
          <Icon icon="mdi:plus" class="mr-1.5 sm:mr-2 text-sm sm:text-base" />
          <span class="hidden xs:inline">Nueva Categoría</span>
          <span class="xs:hidden">Nueva</span>
        </button>
      </div>
    </div>

    <!-- Filtros Compactos -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-5 lg:col-span-5">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Buscar Categorías</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Nombre, descripción..." 
              @input="handleSearch" 
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select 
            v-model="filterStatus" 
            @change="handleFilterChange" 
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="all">Todas</option>
            <option value="activas">Activas</option>
            <option value="inactivas">Inactivas</option>
          </select>
        </div>
        
        <div class="sm:col-span-2 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Ordenar</label>
          <select 
            v-model="sortBy" 
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="nombre">Nombre (A-Z)</option>
            <option value="nombre_desc">Nombre (Z-A)</option>
            <option value="fecha_creacion">Fecha Registro</option>
          </select>
        </div>
        
        <div class="sm:col-span-2 lg:col-span-3 flex justify-end">
          <button 
            @click="clearFilters" 
            class="text-[10px] sm:text-xs font-black text-gray-400 hover:text-indigo-500 transition-colors uppercase flex items-center gap-1.5 p-2 sm:p-3 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-800"
          >
            <Icon icon="mdi:filter-off" class="text-sm sm:text-base" />
            <span class="hidden xs:inline">Limpiar Filtros</span>
            <span class="xs:hidden">Limpiar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4">
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center">
          <div class="p-2 sm:p-2.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
            <Icon icon="mdi:folder-multiple" class="text-base sm:text-xl" />
          </div>
          <div class="ml-3 sm:ml-4">
            <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 font-black uppercase tracking-widest">Total</p>
            <div class="flex items-baseline">
              <p class="text-lg sm:text-xl font-black text-gray-800 dark:text-white">{{ categorias.length }}</p>
              <span class="ml-1.5 sm:ml-2 text-[8px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full font-black">
                {{ filteredCategorias.length }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center">
          <div class="p-2 sm:p-2.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl">
            <Icon icon="mdi:check-circle" class="text-base sm:text-xl" />
          </div>
          <div class="ml-3 sm:ml-4">
            <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 font-black uppercase tracking-widest">Activas</p>
            <div class="flex items-baseline">
              <p class="text-lg sm:text-xl font-black text-gray-800 dark:text-white">{{ estadisticas.activas }}</p>
              <span class="ml-1.5 text-[8px] sm:text-[10px] text-green-600 dark:text-green-400 font-black uppercase tracking-tight">
                {{ estadisticas.porcentajeActivas }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center">
          <div class="p-2 sm:p-2.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl">
            <Icon icon="mdi:close-circle" class="text-base sm:text-xl" />
          </div>
          <div class="ml-3 sm:ml-4">
            <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 font-black uppercase tracking-widest">Inactivas</p>
            <div class="flex items-baseline">
              <p class="text-lg sm:text-xl font-black text-gray-800 dark:text-white">{{ estadisticas.inactivas }}</p>
              <span class="ml-1.5 text-[8px] sm:text-[10px] text-red-600 dark:text-red-400 font-black uppercase tracking-tight">
                {{ estadisticas.porcentajeInactivas }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hidden sm:flex">
        <div class="flex items-center">
          <div class="p-2 sm:p-2.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl">
            <Icon icon="mdi:text-box" class="text-base sm:text-xl" />
          </div>
          <div class="ml-3 sm:ml-4">
            <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 font-black uppercase tracking-widest">Info</p>
            <p class="text-lg sm:text-xl font-black text-gray-800 dark:text-white">{{ estadisticas.conDescripcion }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hidden md:flex">
        <div class="flex items-center">
          <div class="p-2 sm:p-2.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-xl">
            <Icon icon="mdi:percent" class="text-base sm:text-xl" />
          </div>
          <div class="ml-3 sm:ml-4">
            <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 font-black uppercase tracking-widest">Margen</p>
            <p class="text-lg sm:text-xl font-black text-gray-800 dark:text-white">{{ estadisticas.promedioMargen }}%</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-3 sm:mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-2xl p-2.5 sm:p-4">
        <span class="text-[10px] sm:text-sm font-black text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
          <Icon icon="mdi:filter" class="text-sm sm:text-base" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-xs font-black bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs sm:text-sm" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-xs font-black bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'activas' ? 'Activas' : 'Inactivas' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs sm:text-sm" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-[9px] sm:text-xs text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-black flex items-center gap-1"
        >
          <Icon icon="mdi:filter-off" class="text-xs sm:text-sm" />
          <span class="hidden xs:inline">Limpiar filtros</span>
        </button>
      </div>
    </div>

    <!-- Tabla de categorías -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
      <!-- Encabezado de tabla -->
      <div class="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50/50 dark:bg-gray-800/50">
        <div>
          <h2 class="text-sm sm:text-base font-black text-gray-800 dark:text-white">Lista de Categorías</h2>
          <p class="text-[9px] sm:text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-black">
            {{ filteredCategorias.length }} de {{ categorias.length }} categorías
          </p>
        </div>
        <div class="flex items-center space-x-2 text-[9px] sm:text-sm text-gray-500 dark:text-gray-400">
          <Icon icon="mdi:calendar" class="text-xs sm:text-base" />
          <span class="font-black uppercase tracking-widest">Última actualización: {{ formatTime(lastUpdate) }}</span>
        </div>
      </div>

      <!-- Versión Desktop: Tabla -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest">
                <div class="flex items-center space-x-1">
                  <span>Categoría</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'" class="hover:text-indigo-500">
                    <Icon icon="mdi:sort-alphabetical" class="text-sm" />
                  </button>
                </div>
              </th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest">Descripción</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest text-center">Acciones</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest text-center">Estado</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest text-center">Margen</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest text-center">Productos</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-500 dark:text-gray-300 uppercase tracking-widest">Fecha Registro</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="7" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-else-if="filteredCategorias.length === 0">
              <td colspan="7" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:folder-remove" class="text-6xl sm:text-7xl mb-3" />
                  <h4 class="text-base sm:text-xl font-black text-gray-400 tracking-widest">No se encontraron categorías</h4>
                  <p class="text-xs sm:text-sm text-gray-400 mt-2 max-w-md">
                    {{ searchQuery || filterStatus !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay categorías registradas. ¡Crea la primera!' }}
                  </p>
                </div>
              </td>
            </tr>
            <tr 
              v-else 
              v-for="categoria in paginatedCategorias" 
              :key="categoria.idCategoria"
              class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group cursor-pointer"
              @click="openDetailModal(categoria)"
            >
              <td class="px-5 py-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                    <div :class="[
                      'h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shadow-sm',
                      categoria.estado?.toLowerCase() === 'activo'
                        ? 'bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
                    ]">
                      <Icon 
                        icon="mdi:folder" 
                        :class="[
                          'text-base sm:text-xl',
                          categoria.estado?.toLowerCase() === 'activo'
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-gray-500 dark:text-gray-400'
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-3 sm:ml-4">
                    <div class="text-sm sm:text-base font-black text-gray-900 dark:text-white">
                      {{ categoria.nombre }}
                      <span v-if="categoria.estado?.toLowerCase() !== 'activo'" class="ml-2 text-[9px] sm:text-xs text-gray-500">
                        (Inactiva)
                      </span>
                    </div>
                    <div class="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 flex items-center mt-0.5">
                      <Icon icon="mdi:identifier" class="mr-1 text-xs" />
                      ID: {{ categoria.idCategoria }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="text-sm text-gray-900 dark:text-white max-w-md truncate">
                  {{ categoria.descripcion || 'Sin descripción' }}
                </p>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-center gap-1 flex-nowrap">
                  <button
                    @click.stop="openEditModal(categoria)"
                    class="p-1.5 sm:p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" class="text-sm sm:text-base" />
                  </button>
                  <button
                    @click.stop="toggleStatus(categoria)"
                    :class="[
                      'p-1.5 sm:p-2 rounded-lg transition-all shadow-sm flex-shrink-0',
                      categoria.estado?.toLowerCase() === 'activo'
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-600 hover:text-white'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-600 hover:text-white'
                    ]"
                    :title="categoria.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="categoria.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-sm sm:text-base" />
                  </button>
                  <button
                    @click.stop="confirmDelete(categoria)"
                    class="p-1.5 sm:p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" class="text-sm sm:text-base" />
                  </button>
                </div>
              </td>
              <td class="px-5 py-4 text-center">
                <span
                  @click.stop="toggleStatus(categoria)"
                  :class="[
                    'px-2 sm:px-3 py-1 sm:py-1.5 inline-flex text-[8px] sm:text-[10px] leading-4 font-black rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    categoria.estado?.toLowerCase() === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200'
                  ]"
                >
                  <Icon :icon="categoria.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1 text-xs" />
                  {{ categoria.estado || 'Sin estado' }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <div class="text-base sm:text-lg font-black text-amber-600 dark:text-amber-400">
                  {{ formatearMargen(categoria.margen_ganancia_default) }}
                </div>
              </td>
              <td class="px-5 py-4 text-center">
                <div class="text-base sm:text-lg font-black text-blue-600 dark:text-blue-400">
                  {{ categoria.totalProductos || 0 }}
                </div>
              </td>
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="text-xs sm:text-sm text-gray-900 dark:text-white font-black">
                  {{ formatDate(categoria.fecha_creacion || categoria.fechaCreacion) }}
                </div>
                <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 font-black uppercase">
                  {{ formatTime(categoria.fecha_creacion || categoria.fechaCreacion) }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-2 sm:space-y-3 p-2 sm:p-3">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && filteredCategorias.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:folder-remove" class="text-5xl sm:text-6xl mb-3" />
            <h4 class="text-base sm:text-lg font-black text-gray-400 tracking-widest">No hay categorías</h4>
          </div>
        </div>
        
        <div v-for="categoria in paginatedCategorias" :key="categoria.idCategoria" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-3 sm:p-4 hover:shadow-lg transition-all duration-200 cursor-pointer"
             @click="openDetailModal(categoria)">
          
          <div class="flex items-center justify-between mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <div :class="[
                'h-8 w-8 sm:h-10 sm:w-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0',
                categoria.estado?.toLowerCase() === 'activo'
                  ? 'bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
              ]">
                <Icon 
                  icon="mdi:folder" 
                  :class="[
                    'text-sm sm:text-base',
                    categoria.estado?.toLowerCase() === 'activo'
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-500 dark:text-gray-400'
                  ]" 
                />
              </div>
              <div>
                <p class="text-sm sm:text-base font-black text-gray-900 dark:text-white leading-tight">{{ categoria.nombre }}</p>
                <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">ID: {{ categoria.idCategoria }}</p>
              </div>
            </div>
            <span
              :class="[
                'px-2 sm:px-3 py-0.5 sm:py-1 inline-flex text-[7px] sm:text-[9px] leading-4 font-black rounded-full shadow-sm',
                categoria.estado?.toLowerCase() === 'activo'
                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200'
                  : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200'
              ]"
            >
              {{ categoria.estado || 'Sin estado' }}
            </span>
          </div>

          <div class="mb-2 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2 sm:p-3">
            <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
              {{ categoria.descripcion || 'Sin descripción' }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-1 sm:gap-2 mb-2 text-[9px] sm:text-xs">
            <div class="flex items-center gap-1 sm:gap-1.5">
              <Icon icon="mdi:percent" class="text-amber-500 text-sm sm:text-base" />
              <span class="font-black text-gray-700 dark:text-gray-300">Margen: <span class="text-amber-600 dark:text-amber-400">{{ formatearMargen(categoria.margen_ganancia_default) }}</span></span>
            </div>
            <div class="flex items-center gap-1 sm:gap-1.5 justify-end">
              <Icon icon="mdi:package-variant" class="text-blue-500 text-sm sm:text-base" />
              <span class="font-black text-gray-700 dark:text-gray-300">{{ categoria.totalProductos || 0 }} <span class="text-gray-400">productos</span></span>
            </div>
          </div>

          <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 font-black">
              <Icon icon="mdi:calendar" class="inline mr-1 text-xs" />
              {{ formatDate(categoria.fecha_creacion || categoria.fechaCreacion) }}
            </div>
            <div class="flex items-center gap-1 sm:gap-1.5">
              <button
                @click.stop="openEditModal(categoria)"
                class="p-1.5 sm:p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                title="Editar"
              >
                <Icon icon="mdi:pencil" class="text-sm sm:text-base" />
              </button>
              <button
                @click.stop="toggleStatus(categoria)"
                :class="[
                  'p-1.5 sm:p-2 rounded-lg transition-all shadow-sm',
                  categoria.estado?.toLowerCase() === 'activo'
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-600 hover:text-white'
                    : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-600 hover:text-white'
                ]"
                :title="categoria.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar'"
              >
                <Icon :icon="categoria.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-sm sm:text-base" />
              </button>
              <button
                @click.stop="confirmDelete(categoria)"
                class="p-1.5 sm:p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
                title="Eliminar"
              >
                <Icon icon="mdi:delete" class="text-sm sm:text-base" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="filteredCategorias.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[9px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ startItem }} - {{ Math.min(endItem, filteredCategorias.length) }} <span class="mx-1 text-gray-300">|</span> Total: {{ filteredCategorias.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[9px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- ========== MODALES ========== -->
    
    <!-- Modal para crear/editar categoría -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeModal"></div>
        
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden transform transition-all scale-100 animate-zoomIn">
          <!-- Encabezado -->
          <div class="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingCategoria ? 'mdi:pencil-box' : 'mdi:folder-plus'" class="text-white text-xl sm:text-2xl" />
            </div>
            <div>
              <h3 class="text-sm sm:text-base font-black text-white">
                {{ editingCategoria ? 'Editar Categoría' : 'Nueva Categoría' }}
              </h3>
              <p class="text-[9px] sm:text-xs text-white/70 font-bold">
                {{ editingCategoria ? 'Modifica los datos de la categoría' : 'Completa los datos para crear una nueva categoría' }}
              </p>
            </div>
            <button @click="closeModal" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          
          <!-- Formulario -->
          <form @submit.prevent="saveCategoria" class="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
            <div class="space-y-4 sm:space-y-5">
              <!-- Nombre -->
              <div>
                <label class="block text-[10px] sm:text-xs font-black text-gray-400 tracking-widest mb-1.5 sm:mb-2 uppercase">
                  <Icon icon="mdi:tag" class="inline mr-1 text-sm" /> Nombre de la categoría *
                </label>
                <div class="relative">
                  <input
                    v-model="form.nombre"
                    type="text"
                    required
                    maxlength="100"
                    class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl text-xs sm:text-sm font-bold text-gray-900 dark:text-white outline-none transition-all"
                    placeholder="Ej: Computadoras, Accesorios, Software..."
                    :class="{ 'border-red-500': errors.nombre }"
                    @blur="form.nombre = toTitleCase(form.nombre)"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <span class="text-[8px] sm:text-[10px] text-gray-400 font-black">{{ form.nombre.length }}/100</span>
                  </div>
                </div>
                <p v-if="errors.nombre" class="mt-1.5 text-xs text-red-600 dark:text-red-400 font-bold">
                  {{ errors.nombre }}
                </p>
              </div>
              
              <!-- Descripción -->
              <div>
                <label class="block text-[10px] sm:text-xs font-black text-gray-400 tracking-widest mb-1.5 sm:mb-2 uppercase">
                  <Icon icon="mdi:text-box" class="inline mr-1 text-sm" /> Descripción
                </label>
                <div class="relative">
                  <textarea
                    v-model="form.descripcion"
                    rows="3"
                    maxlength="500"
                    class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl text-xs sm:text-sm font-bold text-gray-900 dark:text-white outline-none transition-all resize-none"
                    placeholder="Describe brevemente la categoría..."
                    @blur="form.descripcion = toTitleCase(form.descripcion)"
                  ></textarea>
                  <div class="absolute bottom-2 right-3">
                    <span class="text-[8px] sm:text-[10px] text-gray-400 font-black">{{ form.descripcion.length }}/500</span>
                  </div>
                </div>
              </div>

              <!-- Margen de ganancia -->
              <div>
                <label class="block text-[10px] sm:text-xs font-black text-gray-400 tracking-widest mb-1.5 sm:mb-2 uppercase">
                  <Icon icon="mdi:percent" class="inline mr-1 text-sm" /> Margen de ganancia (%) *
                </label>
                <div class="relative">
                  <input
                    v-model.number="form.margen_ganancia_default"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    required
                    class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl text-xs sm:text-sm font-bold text-gray-900 dark:text-white outline-none transition-all"
                    :class="{ 'border-red-500': errors.margen }"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <span class="text-[8px] sm:text-[10px] text-gray-400 font-black">0-100</span>
                  </div>
                </div>
                <p v-if="errors.margen" class="mt-1.5 text-xs text-red-600 dark:text-red-400 font-bold">
                  {{ errors.margen }}
                </p>
              </div>
              
              <!-- Estado -->
              <div>
                <label class="block text-[10px] sm:text-xs font-black text-gray-400 tracking-widest mb-1.5 sm:mb-2 uppercase">
                  Estado
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <label class="flex items-center p-2.5 sm:p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                    :class="form.estado === 'activo' 
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/30' 
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 hover:border-gray-300'">
                    <input type="radio" v-model="form.estado" value="activo" class="sr-only">
                    <div class="flex items-center w-full gap-2">
                      <div class="h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        :class="form.estado === 'activo' 
                          ? 'border-green-500 bg-green-500' 
                          : 'border-gray-400'">
                        <Icon v-if="form.estado === 'activo'" icon="mdi:check" class="text-white text-[10px]" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="font-bold text-gray-900 dark:text-white text-[10px] sm:text-xs">Activa</p>
                        <p class="text-[7px] sm:text-[9px] text-gray-500 dark:text-gray-400">Disponible</p>
                      </div>
                    </div>
                  </label>
                  
                  <label class="flex items-center p-2.5 sm:p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                    :class="form.estado === 'inactivo' 
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/30' 
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 hover:border-gray-300'">
                    <input type="radio" v-model="form.estado" value="inactivo" class="sr-only">
                    <div class="flex items-center w-full gap-2">
                      <div class="h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        :class="form.estado === 'inactivo' 
                          ? 'border-red-500 bg-red-500' 
                          : 'border-gray-400'">
                        <Icon v-if="form.estado === 'inactivo'" icon="mdi:check" class="text-white text-[10px]" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="font-bold text-gray-900 dark:text-white text-[10px] sm:text-xs">Inactiva</p>
                        <p class="text-[7px] sm:text-[9px] text-gray-500 dark:text-gray-400">No disponible</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <!-- Previsualización -->
              <div v-if="form.nombre" class="p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
                <p class="text-[9px] sm:text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Vista previa</p>
                <div class="flex items-start gap-3">
                  <div :class="[
                    'p-2 sm:p-3 rounded-xl',
                    form.estado === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900'
                  ]">
                    <Icon icon="mdi:folder" 
                      :class="form.estado === 'activo' 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'" 
                      class="text-base sm:text-xl"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-start justify-between gap-1">
                      <p class="font-black text-gray-900 dark:text-white text-sm sm:text-base truncate">{{ form.nombre }}</p>
                      <span :class="[
                        'px-2 py-0.5 text-[7px] sm:text-[9px] font-black rounded-full flex-shrink-0',
                        form.estado === 'activo'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      ]">
                        {{ form.estado === 'activo' ? 'Activa' : 'Inactiva' }}
                      </span>
                    </div>
                    <p class="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400">
                      Margen: {{ form.margen_ganancia_default }}%
                    </p>
                    <p v-if="form.descripcion" class="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                      {{ form.descripcion }}
                    </p>
                    <p v-else class="text-[9px] sm:text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                      Sin descripción
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Botones -->
            <div class="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="button"
                @click="closeModal"
                class="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="saving || !isFormValid"
                :class="[
                  'w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2',
                  isFormValid && !saving
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg shadow-purple-500/30'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                ]"
              >
                <span v-if="saving" class="flex items-center gap-2">
                  <Icon icon="mdi:loading" class="animate-spin text-base" />
                  Guardando...
                </span>
                <span v-else class="flex items-center gap-2">
                  <Icon :icon="editingCategoria ? 'mdi:content-save' : 'mdi:plus-circle'" class="text-base" />
                  {{ editingCategoria ? 'Actualizar' : 'Crear' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modal de detalles -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeDetailModal"></div>
        
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden transform transition-all scale-100 animate-zoomIn">
          <div class="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:folder" class="text-white text-xl sm:text-2xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-black text-white truncate">{{ selectedCategoria?.nombre }}</h3>
              <p class="text-[9px] sm:text-xs text-white/70 font-bold">ID: {{ selectedCategoria?.idCategoria }}</p>
            </div>
            <button @click="closeDetailModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          
          <div v-if="selectedCategoria" class="p-4 sm:p-6 space-y-4">
            <div>
              <label class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Descripción</label>
              <div class="mt-1.5 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <p class="text-sm text-gray-900 dark:text-white">{{ selectedCategoria.descripcion || 'Sin descripción' }}</p>
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Estado</label>
                <div class="mt-1.5">
                  <span :class="[
                    'px-3 py-1 text-[10px] sm:text-xs font-black rounded-full inline-block',
                    selectedCategoria.estado?.toLowerCase() === 'activo'
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  ]">
                    {{ selectedCategoria.estado || 'Sin estado' }}
                  </span>
                </div>
              </div>
              <div>
                <label class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Margen</label>
                <p class="mt-1.5 text-xl sm:text-2xl font-black text-amber-600 dark:text-amber-400">
                  {{ formatearMargen(selectedCategoria.margen_ganancia_default) }}
                </p>
              </div>
            </div>

            <div>
              <label class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Productos</label>
              <p class="mt-1.5 text-xl sm:text-2xl font-black text-blue-600 dark:text-blue-400">
                {{ selectedCategoria.totalProductos || 0 }}
              </p>
            </div>
            
            <div>
              <label class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Fecha de Creación</label>
              <div class="mt-1.5 flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <Icon icon="mdi:calendar" class="text-gray-400 text-sm sm:text-base" />
                <div>
                  <p class="text-xs sm:text-sm text-gray-900 dark:text-white font-bold">{{ formatDate(selectedCategoria.fecha_creacion || selectedCategoria.fechaCreacion) }}</p>
                  <p class="text-[8px] sm:text-[10px] text-gray-400 font-black uppercase">{{ formatTime(selectedCategoria.fecha_creacion || selectedCategoria.fechaCreacion) }}</p>
                </div>
              </div>
            </div>
            
            <div class="pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end gap-2">
              <button
                @click="openEditModal(selectedCategoria); closeDetailModal()"
                class="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md flex items-center justify-center gap-2"
              >
                <Icon icon="mdi:pencil" class="text-sm" />
                Editar
              </button>
              <button
                @click="closeDetailModal"
                class="w-full sm:w-auto px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de Confirmación -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="cancelConfirm"></div>
        
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden transform transition-all scale-100 animate-zoomIn">
          <div class="p-6 sm:p-8 text-center">
            <div :class="[
              'w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4',
              confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 
              confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' : 
              'bg-blue-100 dark:bg-blue-900/30'
            ]">
              <Icon 
                :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' : 
                       confirmConfig.type === 'warning' ? 'mdi:alert' : 
                       'mdi:information'" 
                :class="[
                  'text-2xl sm:text-3xl',
                  confirmConfig.type === 'danger' ? 'text-red-600 dark:text-red-400' : 
                  confirmConfig.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 
                  'text-blue-600 dark:text-blue-400'
                ]"
              />
            </div>
            <h3 class="text-base sm:text-lg font-black text-gray-900 dark:text-white mb-1.5">{{ confirmConfig.title }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium" v-html="confirmConfig.message"></p>
            <p v-if="confirmConfig.detail" class="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 mt-2">{{ confirmConfig.detail }}</p>
            
            <div class="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                @click="cancelConfirm"
                class="w-full sm:flex-1 px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                {{ confirmConfig.cancelText }}
              </button>
              <button
                @click="executeConfirm"
                :class="[
                  'w-full sm:flex-1 px-4 py-2.5 rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all shadow-md',
                  confirmConfig.type === 'danger' ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white' : 
                  confirmConfig.type === 'warning' ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white' : 
                  'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                ]"
              >
                {{ confirmConfig.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import { toTitleCase } from "@/utils/stringUtils.js";

// Importar funciones de la API de categorías
import {
  obtenerCategorias,
  crearCategoria,
  actualizarCategoria,
  eliminarCategoria,
  activarCategoria,
  desactivarCategoria,
  verificarCategoriaPorNombre,
  buscarCategoriasLocal,
  filtrarCategoriasPorEstadoLocal,
  obtenerEstadisticasCategorias,
  ordenarCategorias,
  formatearMargen
} from '@/api/categorias.js';

// Importar generador de reportes
import {
  imprimirReporteCategorias,
  imprimirReporteSimplificado
} from '@/utils/reporteCategoriasGenerator.js';

export default {
  name: 'CategoriasList',
  components: {
    Icon,
  },
  data() {
    return {
      categorias: [],
      loading: false,
      saving: false,
      showModal: false,
      showDetailModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterStatus: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idCategoria: null,
        nombre: '',
        descripcion: '',
        margen_ganancia_default: 30,
        estado: 'activo',
      },
      errors: {},
      editingCategoria: null,
      selectedCategoria: null,
      lastUpdate: new Date(),
    };
  },
  computed: {
    filteredCategorias() {
      let filtered = [...this.categorias];
      
      if (this.searchQuery.trim()) {
        filtered = buscarCategoriasLocal(filtered, this.searchQuery);
      }
      
      filtered = filtrarCategoriasPorEstadoLocal(filtered, this.filterStatus);
      filtered = ordenarCategorias(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticasCategorias(this.categorias);
    },
    
    paginatedCategorias() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCategorias.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredCategorias.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredCategorias.length);
    },
    
    isFormValid() {
      return this.form.nombre.trim() && this.form.margen_ganancia_default >= 0 && this.form.margen_ganancia_default <= 100;
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterStatus !== 'all';
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.loadCategorias();
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    toTitleCase,
    formatearMargen,
    
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },
    
    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) {
        this.confirmConfig.onCancel();
      }
    },
    
    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) {
        this.confirmConfig.onConfirm();
      }
    },
    
    async loadCategorias() {
      this.loading = true;
      try {
        let estado = this.filterStatus;
        if (estado === 'activas') {
          estado = 'activas';
        } else if (estado === 'inactivas') {
          estado = 'inactivo';
        } else {
          estado = 'all';
        }
        
        const response = await obtenerCategorias(estado);
        
        if (response.success) {
          this.categorias = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Categorías cargadas:', this.categorias.length);
        } else {
          throw new Error(response.error || 'Error al cargar categorías');
        }
      } catch (error) {
        console.error('❌ Error al cargar categorías:', error);
        this.toast.error('Error al cargar las categorías: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
      this.loadCategorias();
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openCreateModal() {
      this.editingCategoria = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },
    
    openEditModal(categoria) {
      this.editingCategoria = categoria;
      this.form = {
        idCategoria: categoria.idCategoria,
        nombre: categoria.nombre,
        descripcion: categoria.descripcion || '',
        margen_ganancia_default: categoria.margen_ganancia_default || 30,
        estado: categoria.estado || 'activo',
      };
      this.errors = {};
      this.showModal = true;
    },
    
    openDetailModal(categoria) {
      this.selectedCategoria = categoria;
      this.showDetailModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedCategoria = null;
    },
    
    resetForm() {
      this.form = {
        idCategoria: null,
        nombre: '',
        descripcion: '',
        margen_ganancia_default: 30,
        estado: 'activo',
      };
      this.editingCategoria = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 100) {
        this.errors.nombre = 'El nombre no puede exceder 100 caracteres';
      }
      
      if (this.form.descripcion && this.form.descripcion.length > 500) {
        this.errors.descripcion = 'La descripción no puede exceder 500 caracteres';
      }
      
      if (this.form.margen_ganancia_default < 0 || this.form.margen_ganancia_default > 100) {
        this.errors.margen = 'El margen debe estar entre 0 y 100';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async saveCategoria() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        if (!this.editingCategoria) {
          const verifyResult = await verificarCategoriaPorNombre(this.form.nombre);
          
          if (verifyResult.success && verifyResult.existe) {
            this.errors.nombre = 'Esta categoría ya está registrada';
            this.saving = false;
            return;
          }
        }

        const categoriaData = {
          nombre: this.form.nombre.trim(),
          descripcion: this.form.descripcion.trim() || null,
          margen_ganancia_default: parseInt(this.form.margen_ganancia_default) || 0,
          estado: this.form.estado || 'activo'
        };

        let result;
        if (this.editingCategoria) {
          result = await actualizarCategoria(this.editingCategoria.idCategoria, categoriaData);
        } else {
          result = await crearCategoria(categoriaData);
        }

        if (result.success) {
          await this.loadCategorias();
          this.closeModal();
          this.toast.success(
            this.editingCategoria ? 'Categoría actualizada correctamente' : 'Categoría creada correctamente'
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar categoría:', error);
        this.toast.error(error.message || 'Error al guardar la categoría');
      } finally {
        this.saving = false;
      }
    },
    
    async confirmDelete(categoria) {
      const confirmed = await this.showConfirm({
        title: '¿Eliminar categoría?',
        message: `¿Estás seguro de eliminar <strong>"${categoria.nombre}"</strong>?`,
        detail: 'Esta acción no se puede deshacer.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });
      
      if (confirmed) {
        await this.deleteCategoria(categoria.idCategoria, categoria.nombre);
      }
    },
    
    async deleteCategoria(id, nombre) {
      try {
        const result = await eliminarCategoria(id);
        if (result.success) {
          await this.loadCategorias();
          this.toast.success(`La categoría "${nombre}" ha sido eliminada`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar categoría:', error);
        this.toast.error(error.message || 'Error al eliminar la categoría');
      }
    },
    
    async toggleStatus(categoria) {
      const esActivo = categoria.estado?.toLowerCase() === 'activo';
      
      const confirmed = await this.showConfirm({
        title: `${!esActivo ? 'Activar' : 'Desactivar'} categoría`,
        message: `¿Deseas ${!esActivo ? 'activar' : 'desactivar'} la categoría <strong>"${categoria.nombre}"</strong>?`,
        confirmText: `Sí, ${!esActivo ? 'activar' : 'desactivar'}`,
        cancelText: 'Cancelar',
        type: 'warning'
      });
      
      if (confirmed) {
        try {
          let toggleResult;
          if (!esActivo) {
            toggleResult = await activarCategoria(categoria.idCategoria);
          } else {
            toggleResult = await desactivarCategoria(categoria.idCategoria);
          }
          
          if (toggleResult.success) {
            await this.loadCategorias();
            this.toast.success(`La categoría ha sido ${!esActivo ? 'activada' : 'desactivada'} correctamente`);
          } else {
            throw new Error(toggleResult.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error('Error al cambiar el estado de la categoría');
        }
      }
    },
    
    printFullReport() {
      if (this.filteredCategorias.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        const filtros = {
          estado: this.filterStatus,
          busqueda: this.searchQuery,
          orden: this.sortBy
        };
        
        imprimirReporteCategorias(
          this.filteredCategorias,
          'REPORTE DE CATEGORÍAS - SYSTEM JJ',
          filtros
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printSimplifiedReport() {
      if (this.filteredCategorias.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        imprimirReporteSimplificado(
          this.filteredCategorias,
          'LISTADO DE CATEGORÍAS - SYSTEM JJ'
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printCurrentView() {
      this.printFullReport();
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
      this.loadCategorias();
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

.custom-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Estilos para pantallas extra pequeñas */
@media (max-width: 480px) {
  .xs\:inline { display: inline; }
  .xs\:hidden { display: none; }
}
@media (min-width: 481px) {
  .xs\:inline { display: none; }
  .xs\:hidden { display: inline; }
}
</style>